import React from 'react';
import { Target, TrendingUp, PiggyBank, BookOpen, Calculator, FileText } from 'lucide-react';
import AdSense from '../components/AdSense';
import { useSEO } from '../hooks/useSEO';

const Planning: React.FC = () => {
  useSEO({
    title: 'Planejamento Financeiro Pessoal e Familiar Passo a Passo (2026) | O Teu Dinheiro',
    description: 'Aprenda a montar seu planejamento financeiro em 2026: reserva de emergência, metas de curto e longo prazo, orçamento familiar e independência financeira.',
    keywords: 'planejamento financeiro pessoal, planejamento financeiro familiar, reserva de emergência, metas financeiras 2026, independência financeira',
    url: 'https://www.oteudinheiro.com.br/planejamento'
  });
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-700 text-white py-20">
        <div className="container-custom text-center">
          <Target className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Planejamento Financeiro
          </h1>
          <p className="text-xl md:text-2xl text-primary-100 max-w-3xl mx-auto">
            Organize suas finanças pessoais e construa um futuro financeiro sólido
          </p>
        </div>
      </section>

      {/* Content Sections */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Left Column */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Por que planejar suas finanças?
              </h2>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 p-3 rounded-lg">
                    <Target className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Defina seus objetivos
                    </h3>
                    <p className="text-gray-600">
                      Estabeleça metas claras: comprar uma casa, viajar, aposentadoria ou criar uma reserva de emergência.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-secondary-100 p-3 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-secondary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Controle seus gastos
                    </h3>
                    <p className="text-gray-600">
                      Identifique para onde vai seu dinheiro e encontre oportunidades de economia.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-accent-100 p-3 rounded-lg">
                    <PiggyBank className="w-6 h-6 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Construa patrimônio
                    </h3>
                    <p className="text-gray-600">
                      Aproveite o poder dos juros compostos e multiplique seu dinheiro ao longo do tempo.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Regra 50/30/20
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-primary-50 rounded-lg">
                    <span className="font-medium text-primary-700">50%</span>
                    <span className="text-primary-600">Necessidades essenciais</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
                    <span className="font-medium text-secondary-700">30%</span>
                    <span className="text-secondary-600">Desejos e lazer</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-accent-50 rounded-lg">
                    <span className="font-medium text-accent-700">20%</span>
                    <span className="text-accent-600">Investimentos e economia</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  Esta regra ajuda a distribuir sua renda de forma equilibrada e sustentável.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ad Section */}
      <section className="py-8">
        <div className="container-custom px-4">
          <AdSense />
        </div>
      </section>

      {/* Steps Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Como começar seu planejamento
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Avalie sua situação atual
              </h3>
              <p className="text-gray-600">
                Liste todas as suas receitas, despesas, dívidas e ativos. Seja honesto e detalhado.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-secondary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-secondary-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Defina seus objetivos
              </h3>
              <p className="text-gray-600">
                Estabeleça metas de curto, médio e longo prazo. Seja específico com valores e prazos.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-accent-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-accent-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Crie seu plano de ação
              </h3>
              <p className="text-gray-600">
                Desenvolva estratégias para alcançar cada objetivo e monitore seu progresso regularmente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tools Section */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Ferramentas para seu planejamento
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card p-6">
              <Calculator className="w-12 h-12 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Calculadoras Financeiras
              </h3>
              <p className="text-gray-600 mb-4">
                Calcule juros, parcelas, investimentos e muito mais com nossas ferramentas.
              </p>
              <a href="/calculadoras" className="text-primary-600 hover:text-primary-700 font-medium">
                Acessar calculadoras →
              </a>
            </div>

            <div className="card p-6">
              <FileText className="w-12 h-12 text-secondary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Planilhas Gratuitas
              </h3>
              <p className="text-gray-600 mb-4">
                Baixe planilhas para controle de gastos, planejamento e investimentos.
              </p>
              <a href="/planilhas" className="text-secondary-600 hover:text-secondary-700 font-medium">
                Baixar planilhas →
              </a>
            </div>

            <div className="card p-6">
              <BookOpen className="w-12 h-12 text-accent-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Guias Completos
              </h3>
              <p className="text-gray-600 mb-4">
                Aprenda com nossos artigos e guias sobre finanças pessoais.
              </p>
              <a href="/blog" className="text-accent-600 hover:text-accent-700 font-medium">
                Ler artigos →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-6">
            Comece seu planejamento hoje mesmo!
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Não espere para organizar suas finanças. O melhor momento para começar é agora.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/calculadoras"
              className="btn bg-white text-primary-600 hover:bg-gray-100"
            >
              Usar Calculadoras
            </a>
            <a
              href="/planilhas"
              className="btn bg-transparent text-white border-2 border-white hover:bg-white hover:text-primary-600"
            >
              Baixar Planilhas
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Planning;
